/**
 * Service Worker registration — only kicks in on production-style
 * deployments (HTTPS or localhost). Dev preview over HTTPS qualifies,
 * so the SW is active in the Emergent preview environment too.
 *
 * Failures are logged but never thrown — the app must work without
 * the SW (it's a pure performance enhancement for DICOM caching).
 */

export function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Listen for SW → client messages. When the SW activates a new version
  // and detects that an OLDER version's cache existed, it broadcasts
  // `SW_ACTIVATED`. We reload the page ONCE so the just-deployed chunks
  // are pulled fresh (fixes "hard refresh doesn't apply my changes"
  // after a deploy — the SW was serving stale hashed chunks).
  let reloadedForNewSw = false;
  navigator.serviceWorker.addEventListener('message', async (event) => {
    if (event.data?.type !== 'SW_ACTIVATED' || reloadedForNewSw) return;
    reloadedForNewSw = true;

    // Fetch the freshly-generated version.json to know what changed in
    // this release. `?t=…` avoids reading from HTTP cache.
    let changes = [];
    try {
      const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (r.ok) {
        const j = await r.json();
        if (Array.isArray(j?.changes)) changes = j.changes.slice(0, 4);
      }
    } catch (_) { /* changelog is optional */ }

    // Discreet notification so the user knows why the page is about to reload.
    try {
      const { toast } = await import('sonner');
      if (changes.length > 0) {
        // Sonner accepts JSX in description; use plain string with bullets so
        // this file stays free of JSX (it's imported before React is up).
        toast('Nueva versión disponible · Actualizando…', {
          description: 'Novedades:\n• ' + changes.join('\n• '),
          duration: 3800,
          position: 'bottom-right',
          className: 'bg-emerald-50 text-emerald-900 border border-emerald-200 whitespace-pre-line',
        });
      } else {
        toast('Nueva versión disponible · Actualizando…', {
          duration: 1400,
          position: 'bottom-right',
          className: 'bg-emerald-50 text-emerald-900 border border-emerald-200',
        });
      }
    } catch (_) { /* toast is optional; reload still happens */ }

    // Give the toast enough time to be seen. Longer window when there is
    // a changelog to read.
    const wait = changes.length > 0 ? 3800 : 1500;
    setTimeout(() => { window.location.reload(); }, wait);
  });

  // Defer registration until after `load` so the SW install doesn't
  // compete with the initial page paint.
  window.addEventListener('load', () => {
    navigator.serviceWorker
      // `updateViaCache: 'none'` obliga al navegador a IGNORAR la
      // caché HTTP al pedir `/sw.js` — imprescindible para PWAs
      // instaladas en pantalla de inicio (iOS/Android), donde el
      // service worker cacheado puede seguir sirviendo bundles JS
      // antiguos tras un despliegue.  Sin esto, el "sw.js" nuevo
      // nunca se descarga y `activate` no purga los caches viejos.
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        // Auto-update: when a new SW takes over, reload the page once
        // the worker is in `activated` state so users see the latest
        // assets without manual hard-refresh.
        if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            // Cuando el SW nuevo pasa a "installed" y ya hay un
            // controller (SW viejo), le mandamos SKIP_WAITING para
            // que tome control inmediato — el SW nuevo dispara luego
            // SW_ACTIVATED y el listener superior recarga la página.
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });

        // Chequeo explícito al arrancar la PWA — cubre el caso de
        // "usuario abre la app instalada tras un despliegue": sin
        // esta línea, el navegador podría no revalidar el SW hasta
        // horas después.
        try { reg.update(); } catch (_) { /* silent */ }

        // Y un ping periódico cada 30 min por si el usuario deja la
        // PWA abierta un turno completo — captura despliegues
        // intermedios sin cerrar la app.
        setInterval(() => { try { reg.update(); } catch (_e) { /* noop */ } }, 30 * 60 * 1000);
      })
      .catch((err) => {
        // Silent fallback — app works without SW.
        // eslint-disable-next-line no-console
        console.warn('[sw] registration failed:', err?.message || err);
      });
  });
}
